import React, {useState} from "react";
import {
	Box,
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	IconButton,
	MenuItem,
	Select,
	TableCell,
	TableRow,
	TextField,
} from "@mui/material";
import {Code, Delete} from "@mui/icons-material";
import Editor from "@monaco-editor/react";

export interface SettingRowData {
	id?: number;
	order: number;
	type: number;
	code?: string;
	settings: string;
	description: string | null;
}

interface Props {
	setting: SettingRowData;
	typeOptions: { value: number; label: string }[];
	showCode?: boolean;
	onChange: (updated: SettingRowData) => void;
	onDelete: () => void;
}

export function SettingRowEditor({setting, typeOptions, showCode, onChange, onDelete}: Props) {
	const [jsonDialogOpen, setJsonDialogOpen] = useState(false);
	const [jsonDraft, setJsonDraft] = useState(setting.settings);
	const [jsonError, setJsonError] = useState<string | null>(null);

	const openJson = () => {
		setJsonDraft(setting.settings);
		setJsonError(null);
		setJsonDialogOpen(true);
	};

	const formatJson = () => {
		try {
			const formatted = JSON.stringify(JSON.parse(jsonDraft), null, 2);
			setJsonDraft(formatted);
			setJsonError(null);
		} catch (e: any) {
			setJsonError(e.message);
		}
	};

	const saveJson = () => {
		try {
			JSON.parse(jsonDraft);
		} catch (e: any) {
			setJsonError(e.message);
			return;
		}
		onChange({...setting, settings: jsonDraft});
		setJsonDialogOpen(false);
	};

	return (
		<>
			<TableRow>
				<TableCell style={{width: 80}}>
					<TextField
						size="small"
						type="number"
						value={setting.order}
						onChange={e => onChange({...setting, order: parseInt(e.target.value) || 0})}
						style={{width: 70}}
					/>
				</TableCell>
				<TableCell style={{width: 200}}>
					<Select
						size="small"
						value={setting.type}
						onChange={e => onChange({...setting, type: e.target.value as number})}
						style={{minWidth: 180}}
					>
						{typeOptions.map(opt => (
							<MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
						))}
					</Select>
				</TableCell>
				{showCode && (
					<TableCell style={{width: 150}}>
						<TextField
							size="small"
							value={setting.code ?? ""}
							placeholder="Code"
							onChange={e => onChange({...setting, code: e.target.value})}
						/>
					</TableCell>
				)}
				<TableCell>
					<TextField
						size="small"
						fullWidth
						value={setting.description ?? ""}
						placeholder="Description"
						onChange={e => onChange({...setting, description: e.target.value || null})}
					/>
				</TableCell>
				<TableCell style={{width: 100}}>
					<IconButton size="small" title="Edit JSON" onClick={openJson}>
						<Code />
					</IconButton>
					<IconButton size="small" color="error" onClick={onDelete}>
						<Delete />
					</IconButton>
				</TableCell>
			</TableRow>

			<Dialog open={jsonDialogOpen} onClose={() => setJsonDialogOpen(false)} maxWidth="xl" fullWidth
				PaperProps={{style: {height: "85vh"}}}>
				<DialogTitle>Edit Settings JSON</DialogTitle>
				<DialogContent style={{display: "flex", flexDirection: "column", padding: "8px 24px"}}>
					{jsonError && (
						<Box style={{color: "#d32f2f", fontSize: 13, marginBottom: 6}}>
							Invalid JSON: {jsonError}
						</Box>
					)}
					<Box style={{flex: 1, minHeight: 0}}>
						<Editor
							height="100%"
							language="json"
							value={jsonDraft}
							onChange={value => {
								setJsonDraft(value ?? ""); setJsonError(null); 
							}}
							options={{
								minimap: {enabled: false},
								scrollBeyondLastLine: false,
								fontSize: 13,
							}}
						/>
					</Box>
				</DialogContent>
				<DialogActions>
					<Button onClick={formatJson} variant="outlined">Format</Button>
					<Box style={{flex: 1}} />
					<Button onClick={() => setJsonDialogOpen(false)}>Cancel</Button>
					<Button variant="contained" onClick={saveJson}>Apply</Button>
				</DialogActions>
			</Dialog>
		</>
	);
}